import { ScenarioFormProvider, useScenarioForm } from './contexts';
import styles from './dca-scenario-form.module.scss';
import { AreaPanel } from './area-panel';
import { GlobalConfigs } from './global-configs';
import { GeneralConfigs } from './general-configs';
import { SegmentationConfigs } from './segmentation-configs';
import { OutlierDetectionConfigs } from './outlier-detection-configs';
import { PeakRateDetectionConfigs } from './peak-rate-detection-configs';
import { ProductionSteamConfigs } from './production-stream-configs/production-stream-configs';
import type { GlobalConfig } from './types';
import { useEffect, useState } from 'react';
import { PageHeader } from 'components/page-header';
import { InlineEdit } from 'components/inline-edit';
import { useScenarioNameEditor } from './use-scenario-name-editor';
import {
  Button,
  InlineLoading,
  InlineNotification,
  Loading,
} from '@carbon/react';
import { startCase } from 'lodash-es';
import { AreaConfigs } from './area-configs';
import { DevelopmentType } from 'types/dca-scenarios-api/responses';
import { useHistory, useParams } from 'react-router-dom';
import { cloneDcaScenarioAtom, dcaScenarioDataAtom } from './atoms';
import { useAtom, useAtomValue } from 'jotai';
import { AddScenarioNameModal } from './add-scenario-name-modal';
import { EditNotes } from './edit-notes';
import { EventsPanel, EventsPanelOpenButton } from 'components/events-panel';
import { AreaMap } from './area-map';

interface DcaScenarioFormProps {
  initialValues?: GlobalConfig;
  commentsUrl?: string;
  eventsUrl?: string;
  areaConfigsUrl?: string;
  pageHref?: string;
  scenarioName?: string;
  developmentType?: DevelopmentType;
}

export const DcaScenarioForm = ({
  commentsUrl,
  eventsUrl,
  areaConfigsUrl,
  initialValues,
  pageHref = '/dca_scenarios/new',
  scenarioName,
}: DcaScenarioFormProps) => {
  const [editNote, setEditNote] = useState<boolean>(false);
  const [isAddScenarioNameModalOpen, setIsAddScenarioNameModalOpen] =
    useState(false);
  const dcaScenarioData = useAtomValue(dcaScenarioDataAtom);
  const {
    savedName,
    editedName,
    changeName,
    abandonEdit,
    saveName,
    savingName,
    nameError,
    dismissNameError,
  } = useScenarioNameEditor(pageHref, scenarioName ?? '');

  useEffect(() => {
    if (!dcaScenarioData.loading) {
      setEditNote(false);
    }
  }, [dcaScenarioData.loading]);

  return (
    <ScenarioFormProvider
      commentsUrl={commentsUrl}
      eventsUrl={eventsUrl}
      areaConfigsUrl={areaConfigsUrl}
    >
      {dcaScenarioData.loading && (
        <div className={styles.loadingContainer}>
          <Loading withOverlay={false} />
        </div>
      )}
      <FormHeader
        pageHref={pageHref}
        scenarioName={savedName || undefined}
        editedName={editedName}
        onNameChange={changeName}
        onNameSave={saveName}
        onNameAbandon={abandonEdit}
        savingName={savingName}
        showEventsPanel={!!(eventsUrl && commentsUrl)}
        setIsAddScenarioNameModalOpen={setIsAddScenarioNameModalOpen}
      />

      {nameError && (
        <div className={styles.inlineNotificationsContainer}>
          <InlineNotification
            className={styles.inlineNotification}
            kind="error"
            title="Rename Failed"
            lowContrast
            subtitle={nameError}
            onCloseButtonClick={dismissNameError}
          />
        </div>
      )}

      <FormError />

      <EditNotes editNote={editNote} setEditNote={setEditNote} />

      <AreaMap />

      <div className={styles.layoutContainer}>
        <AreaPanel
          resourceUrl={areaConfigsUrl}
          setIsAddScenarioNameModalOpen={setIsAddScenarioNameModalOpen}
        />
        <ConfigForms
          initialValues={initialValues}
          setIsAddScenarioNameModalOpen={setIsAddScenarioNameModalOpen}
        />
      </div>

      <EventsPanel
        header={savedName}
        title="DCA Scenario"
        helperText="DCA Scenario context allows you to share information about a scenario."
        eventsHref={eventsUrl}
        commentsHref={commentsUrl}
      />

      {isAddScenarioNameModalOpen && (
        <AddScenarioNameModal
          onClose={() => {
            setIsAddScenarioNameModalOpen(false);
          }}
        />
      )}
    </ScenarioFormProvider>
  );
};

interface ConfigFormsProps {
  initialValues?: GlobalConfig;
  setIsAddScenarioNameModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const ConfigForms = ({
  initialValues,
  setIsAddScenarioNameModalOpen,
}: ConfigFormsProps) => {
  const { setFieldBaseline, selectedConfig, discardCount } = useScenarioForm();

  useEffect(() => {
    if (initialValues) {
      setFieldBaseline('globalConfig', initialValues);
    }
  }, [initialValues, setFieldBaseline]);

  return (
    <div className={styles.configFormsContainer}>
      {selectedConfig?.type === 'global' && <GlobalConfigs />}
      {(selectedConfig?.areaGeometryId ||
        selectedConfig?.id?.toString() === 'local') && (
        <AreaConfigs
          key={discardCount}
          setIsAddScenarioNameModalOpen={setIsAddScenarioNameModalOpen}
        />
      )}
      <GeneralConfigs />
      <SegmentationConfigs />
      <OutlierDetectionConfigs />
      <PeakRateDetectionConfigs />
      <ProductionSteamConfigs />
    </div>
  );
};

interface FormHeaderProps {
  pageHref?: string;
  scenarioName?: string;
  editedName: string;
  onNameChange: (name: string) => void;
  onNameSave: (name: string) => void;
  onNameAbandon: () => void;
  savingName: boolean;
  showEventsPanel?: boolean;
  setIsAddScenarioNameModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const FormHeader = ({
  pageHref = '/dca_scenarios/new',
  scenarioName,
  editedName,
  onNameChange,
  onNameSave,
  onNameAbandon,
  savingName,
  showEventsPanel = false,
  setIsAddScenarioNameModalOpen,
}: FormHeaderProps) => {
  const { id: scenarioId } = useParams<{ id: string }>();
  const {
    form,
    isFormModified,
    discardFormChanges,
    handleSubmitForm,
    selectedConfig,
    validConfigState,
  } = useScenarioForm();
  const [cloneDcaScenarioState, cloneDcaScenario] =
    useAtom(cloneDcaScenarioAtom);
  const history = useHistory();

  const handleCloneScenario = () => {
    cloneDcaScenario({
      type: 'POST',
      url: `/dca_scenarios/${scenarioId}/clone`,
      data: {},
      onSuccess: (data: any) => {
        history.push(`/dca_scenarios/${data.id}`);
      },
    });
  };

  return (
    <>
      <form.Subscribe
        selector={(state) => {
          const isModified = isFormModified(state.values);
          let canSubmit =
            isModified && state.canSubmit && !!state.values.generalConfig?.id;

          if (state.values.areaConfigs?.areaGeometryId) {
            canSubmit =
              canSubmit && (validConfigState ? validConfigState.isValid : true);
          }

          return {
            isModified,
            canSubmit,
          };
        }}
      >
        {({ isModified, canSubmit }) => (
          <PageHeader
            breadcrumbs={[
              ['/dca_scenarios', 'DCA Scenarios'],
              [pageHref, scenarioName || 'Create New DCA Scenario'],
            ]}
            title={
              scenarioName ? (
                <span className={styles.scenarioTitle}>
                  <InlineEdit
                    id={`dca-scenario-${scenarioId}-title`}
                    className={styles.inlineEdit}
                    value={editedName}
                    onChange={(val: string) => onNameChange(val)}
                    onCancel={(initialVal: string) => {
                      onNameChange(initialVal);
                      // v11 bug: cancel button doesn't exit edit mode (no
                      // setFocused(false)). Force blur so the component's
                      // onBlurHandler exits edit mode.
                      requestAnimationFrame(() => {
                        if (document.activeElement instanceof HTMLElement) {
                          document.activeElement.blur();
                        }
                      });
                    }}
                    onBlur={onNameAbandon}
                    labelText="DCA Scenario title"
                    editLabel="Edit scenario title"
                    saveLabel="Save"
                    cancelLabel="Cancel"
                    editAlwaysVisible
                    inheritTypography
                    onSave={() => onNameSave(editedName)}
                    placeholder="Scenario title"
                    size="md"
                  />
                  {savingName && (
                    <InlineLoading
                      className={styles.savingName}
                      description="Saving…"
                    />
                  )}
                </span>
              ) : (
                'Create New DCA Scenario'
              )
            }
            actions={
              <div className={styles.actionButtonsContainer}>
                <Button
                  kind="secondary"
                  size="md"
                  disabled={!isModified || cloneDcaScenarioState.loading}
                  onClick={discardFormChanges}
                >
                  Cancel
                </Button>
                <Button
                  size="md"
                  disabled={!canSubmit}
                  kind="primary"
                  onClick={() => {
                    if (!scenarioId) {
                      setIsAddScenarioNameModalOpen(true);
                    } else {
                      handleSubmitForm({
                        dcaScenarioId: scenarioId,
                        areaConfigId: selectedConfig?.href
                          ? selectedConfig.id
                          : null,
                      });
                    }
                  }}
                >
                  Save
                </Button>

                {scenarioName && (
                  <Button
                    className={styles.cloneScenarioButton}
                    size="md"
                    disabled={isModified || cloneDcaScenarioState.loading}
                    kind="primary"
                    onClick={handleCloneScenario}
                  >
                    {cloneDcaScenarioState.loading ? (
                      <InlineLoading />
                    ) : (
                      'Clone DCA Scenario'
                    )}
                  </Button>
                )}

                {showEventsPanel && <EventsPanelOpenButton />}
              </div>
            }
          />
        )}
      </form.Subscribe>
    </>
  );
};

const FormError = () => {
  const { form } = useScenarioForm();
  const dcaScenarioDataState = useAtomValue(dcaScenarioDataAtom);

  return (
    <form.Subscribe
      selector={(state) => {
        const errors = state.errors;
        const sectionsWithErrors = Array.from(
          new Set(
            errors
              .flat()
              .filter((error): error is string => typeof error === 'string')
              .flatMap((error) => error.split(', '))
              .map((section) =>
                startCase(
                  section
                    .replace('StreamConfigs', ' Model Config')
                    .replace('Config', ' Config')
                )
              )
          )
        );

        const formMessage =
          sectionsWithErrors.length !== 0
            ? `${sectionsWithErrors.join(', ')} ${
                sectionsWithErrors.length > 1 ? 'are' : 'is'
              } having issues. Please review them.`
            : null;

        return { formMessage, generalConfig: state.values.generalConfig };
      }}
    >
      {({ formMessage, generalConfig }) => {
        // if (!formMessage) return null;
        return (
          <div className={styles.inlineNotificationsContainer}>
            {!generalConfig?.id && (
              <InlineNotification
                className={styles.inlineNotification}
                kind="warning"
                title="General Config"
                hideCloseButton
                lowContrast
                subtitle="Please select or create a general config to save"
              />
            )}

            {formMessage && (
              <InlineNotification
                className={styles.inlineNotification}
                kind="error"
                title="Form Error"
                hideCloseButton
                lowContrast
                subtitle={formMessage}
              />
            )}

            {dcaScenarioDataState.error && (
              <InlineNotification
                className={styles.inlineNotification}
                kind="error"
                title="Error"
                hideCloseButton
                lowContrast
                subtitle={dcaScenarioDataState.error}
              />
            )}
          </div>
        );
      }}
    </form.Subscribe>
  );
};
